import React, { useState } from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, useTheme, useMediaQuery, Divider, Collapse } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import ArticleIcon from '@mui/icons-material/Article';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import CategoryIcon from '@mui/icons-material/Category';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { authService } from '../../services/authService';

const menuItems = [
  { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/checkinout', label: 'เวลาเข้า-ออกงาน', icon: <AccessTimeIcon /> },
  { path: '/employees', label: 'พนักงาน', icon: <PeopleIcon /> },
  { path: '/projects', label: 'โครงการ', icon: <BusinessIcon /> },
  { path: '/documents', label: 'เอกสาร', icon: <ArticleIcon /> },
  { 
    label: 'สื่อการเรียนรู้', 
    icon: <SchoolIcon />,
    submenu: [
      { path: '/learning/categories', label: 'หมวดหมู่', icon: <CategoryIcon /> },
      { path: '/learning', label: 'สื่อการเรียนรู้', icon: <MenuBookIcon /> },
    ]
  },
  { path: '/users', label: 'ผู้ใช้งาน', icon: <PersonIcon /> },
];

const Sidebar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const isPathActive = (path: string) => {
    // ตรวจสอบกรณีเพิ่มผู้ใช้ใหม่
    if (location.pathname === '/users/add') {
      const isFromEmployee = new URLSearchParams(location.search).get("from") === "employee";
      if (isFromEmployee && path === '/employees') {
        return true;
      }
      if (!isFromEmployee && path === '/users') {
        return true;
      }
      return false;
    }

    // ตรวจสอบกรณีแก้ไขผู้ใช้
    if (location.pathname.startsWith('/users/') && location.pathname.endsWith('/edit')) {
      const isFromEmployee = location.state?.from === 'employees';
      if (isFromEmployee && path === '/employees') {
        return true;
      }
      if (!isFromEmployee && path === '/users') {
        return true;
      }
      return false;
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  const handleSubmenuClick = (label: string) => {
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  const isSubmenuOpen = (label: string) => openSubmenu === label;

  const isSubmenuActive = (submenuItems: { path: string }[]) => {
    return submenuItems.some(item => isPathActive(item.path));
  };

  return (
    <Box>
      {/* Logo */}
      <Box
        component="img"
        src="/images/logo-teamcm.webp"
        alt="TEAM-OM"
        sx={{
          width: '100%',
          maxWidth: 130,
          height: 'auto',
          mx: 'auto',
          display: 'block',
          my: 2,
          px: 2,
        }}
      />

      {/* Menu Items */}
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.label}>
            {item.submenu ? (
              <>
                <ListItemButton
                  onClick={() => handleSubmenuClick(item.label)}
                  selected={isSubmenuActive(item.submenu)}
                  sx={{
                    py: { xs: 1, sm: 1.5 },
                    px: 2,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'white',
                    minWidth: { xs: 40, sm: 45 },
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.2rem', sm: '1.5rem' },
                    },
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: isSubmenuActive(item.submenu) ? 500 : 400,
                      },
                    }}
                  />
                  {isSubmenuOpen(item.label) ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={isSubmenuOpen(item.label)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.submenu.map((subItem) => (
                      <ListItemButton
                        key={subItem.path}
                        selected={isPathActive(subItem.path)}
                        onClick={() => navigate(subItem.path)}
                        sx={{
                          py: { xs: 0.75, sm: 1 },
                          pl: { xs: 4, sm: 5 },
                          pr: 2,
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                            },
                          },
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ 
                          color: 'white',
                          minWidth: { xs: 35, sm: 40 },
                          '& .MuiSvgIcon-root': {
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                          },
                        }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={subItem.label} 
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              fontWeight: isPathActive(subItem.path) ? 500 : 400,
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItemButton
                selected={isPathActive(item.path!)}
                onClick={() => navigate(item.path!)}
                sx={{
                  py: { xs: 1, sm: 1.5 },
                  px: 2,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: 'white',
                  minWidth: { xs: 40, sm: 45 },
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1.2rem', sm: '1.5rem' },
                  },
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label} 
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: isPathActive(item.path!) ? 500 : 400,
                    },
                  }}
                />
              </ListItemButton>
            )}
          </React.Fragment>
        ))}
        
        <Divider sx={{ my: 2, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
        
        {/* Logout Button */}
        <ListItemButton
          onClick={handleLogout}
          sx={{
            py: { xs: 1, sm: 1.5 },
            px: 2,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: 'white',
            minWidth: { xs: 40, sm: 45 },
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
            },
          }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="ออกจากระบบ" 
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
        </ListItemButton>
      </List>
    </Box>
  );
};

export default Sidebar; 